// src/pages/PrivacyPolicy.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  BookOpenIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const EFFECTIVE_DATE = '25 July 2026';

const PolicySection = ({
  icon: Icon,
  title,
  children,
}) => {
  return (
    <section className="border-t border-gray-100 px-5 py-6 first:border-t-0 sm:px-8 sm:py-7">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary-50">
          <Icon className="h-5 w-5 text-primary-700" />
        </div>

        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
            {title}
          </h2>

          <div className="mt-2 space-y-3 text-sm leading-6 text-gray-600">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

const PolicyList = ({ children }) => {
  return (
    <ul className="space-y-2">
      {children}
    </ul>
  );
};

const PolicyItem = ({ children }) => {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircleIcon className="mt-1 h-4 w-4 flex-none text-primary-600" />

      <span>{children}</span>
    </li>
  );
};

export default function PrivacyPolicy() {
  return (
    <div
      className="
        min-h-screen bg-gray-50 text-gray-900
        [padding-top:env(safe-area-inset-top)]
        [padding-bottom:env(safe-area-inset-bottom)]
      "
    >
      {/* Top navigation */}
      <header className="sticky top-0 z-20 border-b border-gray-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center px-4 sm:px-6">
          <Link
            to="/"
            aria-label="PawaLibrary home"
            className="inline-flex items-center gap-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <img
              src="/assets/app-logo.png"
              alt=""
              className="h-9 w-9 object-contain"
            />

            <span className="text-base font-bold tracking-tight text-primary-700">
              PawaLibrary
            </span>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Introduction */}
          <div className="px-5 py-7 sm:px-8 sm:py-9">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50">
              <ShieldCheckIcon className="h-6 w-6 text-primary-700" />
            </div>

            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-primary-700">
              Your privacy matters
            </p>

            <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Privacy Policy
            </h1>

            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
              This Privacy Policy explains
              what information PawaLibrary
              collects, why we use it and the
              choices available to you.
            </p>

            <p className="mt-4 text-xs text-gray-500">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </div>

          {/* Information collected */}
          <PolicySection
            icon={UserIcon}
            title="Information we collect"
          >
            <p>
              We collect only the information
              needed to provide and protect
              PawaLibrary.
            </p>

            <PolicyList>
              <PolicyItem>
                <strong className="font-medium text-gray-800">
                  Account information:
                </strong>{' '}
                your name, email address,
                password in encrypted form,
                profile details and account
                role.
              </PolicyItem>

              <PolicyItem>
                <strong className="font-medium text-gray-800">
                  Purchase information:
                </strong>{' '}
                books purchased, payment
                references, transaction status,
                amount, currency and purchase
                date.
              </PolicyItem>

              <PolicyItem>
                <strong className="font-medium text-gray-800">
                  Library activity:
                </strong>{' '}
                favourites, reading progress,
                downloads and interactions with
                books or library features.
              </PolicyItem>

              <PolicyItem>
                <strong className="font-medium text-gray-800">
                  Technical information:
                </strong>{' '}
                device, app, diagnostic, crash
                and performance information
                when those reporting features
                are enabled.
              </PolicyItem>

              <PolicyItem>
                <strong className="font-medium text-gray-800">
                  School association:
                </strong>{' '}
                students using a school account
                may be connected to the school
                that created or manages their
                account.
              </PolicyItem>
            </PolicyList>
          </PolicySection>

          {/* Data use */}
          <PolicySection
            icon={BookOpenIcon}
            title="How we use information"
          >
            <PolicyList>
              <PolicyItem>
                Create, authenticate and manage
                your PawaLibrary account.
              </PolicyItem>

              <PolicyItem>
                Provide access to books,
                purchases, downloads,
                favourites and reading
                progress.
              </PolicyItem>

              <PolicyItem>
                Process and verify premium-book
                payments.
              </PolicyItem>

              <PolicyItem>
                Maintain app security, prevent
                fraud and investigate technical
                problems.
              </PolicyItem>

              <PolicyItem>
                Improve app reliability,
                performance and user
                experience.
              </PolicyItem>

              <PolicyItem>
                Meet legal, security,
                accounting and regulatory
                obligations.
              </PolicyItem>
            </PolicyList>
          </PolicySection>

          {/* Payments and third parties */}
          <PolicySection
            icon={BanknotesIcon}
            title="Payments and service providers"
          >
            <p>
              PawaLibrary may use Monime to
              process or verify payments for
              premium books. Information
              required to complete a payment,
              such as a payment reference,
              amount and transaction status,
              may be processed by Monime.
            </p>

            <p>
              We may also use trusted hosting,
              database, security and technical
              service providers to operate the
              app. They may process information
              only as needed to provide their
              services to PawaLibrary.
            </p>

            <p>
              We do not sell personal
              information or share it with
              advertising companies for
              targeted advertising.
            </p>
          </PolicySection>

          {/* Security */}
          <PolicySection
            icon={LockClosedIcon}
            title="How we protect information"
          >
            <p>
              Information transmitted between
              the app and our servers is
              protected using HTTPS encryption.
              Passwords are stored in hashed
              form and are not stored as plain
              text.
            </p>

            <p>
              We use reasonable technical and
              organisational safeguards to
              protect information. However, no
              internet service or storage
              system can guarantee complete
              security.
            </p>
          </PolicySection>

          {/* Retention */}
          <PolicySection
            icon={ShieldCheckIcon}
            title="Data retention"
          >
            <p>
              We retain account information
              while your account is active and
              for as long as needed to provide
              the service.
            </p>

            <p>
              When you delete your account or
              library data, applicable personal
              information is removed or
              anonymised. Certain payment,
              transaction, security, backup or
              school-controlled records may be
              retained when required for legal,
              accounting, fraud-prevention or
              education purposes.
            </p>
          </PolicySection>

          {/* Account deletion */}
          <PolicySection
            icon={UserIcon}
            title="Your choices and account deletion"
          >
            <p>
              You can request deletion of your
              library activity without closing
              your account. You can also request
              permanent account deletion.
            </p>

            <p>
              Independent PawaLibrary accounts
              can be deleted after password
              confirmation. Accounts connected
              to a school may require review so
              that school-controlled academic
              records are not removed
              incorrectly.
            </p>
          </PolicySection>

          {/* Children and students */}
          <PolicySection
            icon={BookOpenIcon}
            title="Children and student accounts"
          >
            <p>
              PawaLibrary may be used by
              students, including students whose
              accounts are created or managed by
              their schools. School-linked
              account information may be managed
              by the relevant school.
            </p>

            <p>
              PawaLibrary does not knowingly use
              children’s personal information
              for targeted advertising. A parent,
              guardian or authorised school
              representative may contact us
              regarding a student’s information.
            </p>
          </PolicySection>

          {/* Policy updates */}
          <PolicySection
            icon={ShieldCheckIcon}
            title="Changes to this policy"
          >
            <p>
              We may update this Privacy Policy
              when our services, legal
              obligations or data practices
              change. The latest version will
              always be published on this page
              with its effective date.
            </p>
          </PolicySection>

          {/* Contact */}
          <PolicySection
            icon={EnvelopeIcon}
            title="Contact us"
          >
            <p>
              Contact PawaLibrary if you have a
              privacy question, want to exercise
              your data rights or need help with
              account deletion.
            </p>

            <a
              href="mailto:support@pawalibrary.com?subject=PawaLibrary%20privacy%20request"
              className="inline-flex break-all font-semibold text-primary-700 hover:underline"
            >
              support@pawalibrary.com
            </a>
          </PolicySection>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-6 text-xs text-gray-500">
          <span>
            © {new Date().getFullYear()}{' '}
            PawaLibrary
          </span>

          <a
            href="mailto:support@pawalibrary.com"
            className="font-medium text-primary-700 hover:underline"
          >
            Support
          </a>
        </div>
      </main>
    </div>
  );
}